import type {
    ColumnsConfigType,
    ContactsEnrichmentSelectedColumns,
    EnrichmentConfigItem,
    PostContactsCsvProps,
    PostContactsCsvPropsFormData,
} from "app/pages/sales-intelligence/modules/enrichment/store/type.ts";
import type {
    ConfigField,
    CsvExportColumnCategory,
} from "app/pages/sales-intelligence/modules/enrichment/types/columns";
import type { ContactsHeaderSelection } from "app/pages/sales-intelligence/sub-modules/management/components/types";

type FieldCategoryMap = Map<string, CsvExportColumnCategory>;

const buildFieldCategoryMap = (columnsConfig: readonly ColumnsConfigType[]): FieldCategoryMap => {
    const map = new Map<string, CsvExportColumnCategory>();

    columnsConfig.forEach(({ name, fields }) => {
        fields.forEach(({ field }) => {
            map.set(field.name, name);
        });
    });

    return map;
};

type SortConfigFieldsOptions = {
    defaultFields: readonly ConfigField[];
    columnsConfig: readonly ColumnsConfigType[];
};

/**
 * Sorts config fields by category using columnsConfig from API
 */
export const sortConfigFieldsByName = ({ defaultFields, columnsConfig }: SortConfigFieldsOptions) => {
    const fieldCategoryMap = buildFieldCategoryMap(columnsConfig);

    return defaultFields.reduce<Record<CsvExportColumnCategory, ConfigField[]>>(
        (acc, defaultField) => {
            const category = fieldCategoryMap.get(defaultField.name);

            if (category) {
                acc[category].push(defaultField);
            }

            return acc;
        },
        {
            companyAndWebsite: [],
            incomingTraffic: [],
            technologies: [],
            visitorsAndEngagement: [],
            marketingChannels: [],
            apps: [],
            contacts: [],
            adSpend: [],
            signals: [],
        },
    );
};

export const mapContactSelectedColumnsToRegularColumns = (
    columns: ContactsEnrichmentSelectedColumns,
): EnrichmentConfigItem[] => {
    return [
        ...(columns.selectedColumns?.map((name) => ({
            name,
            alias: name,
            type: "regular" as EnrichmentConfigItem["type"],
        })) ?? []),
        ...(columns.accountSelectedColumns?.map((name) => ({
            name,
            alias: name,
            type: "regular" as EnrichmentConfigItem["type"],
        })) ?? []),
    ];
};

export const mapContactEnrichmentToFormData = (
    { columns, parsingResult }: PostContactsCsvProps,
    selectedHeaders: ContactsHeaderSelection,
    fileName: string,
): PostContactsCsvPropsFormData => {
    const rows: string[][] = [];

    for (let i = 0; i < parsingResult.totalRowCount; i++) {
        const row: string[] = [];

        parsingResult.headerNames.forEach((headerName) => {
            const value = parsingResult.fileParsed[headerName][i] ?? "";
            row.push(value);
        });

        rows.push(row);
    }

    const headers = parsingResult.headerNames.reduce(
        (acc: { key: string; label: string }[], header: string) => {
            const key = Object.entries(selectedHeaders).reduce((acc: string, [key, value]) => {
                if (value === header) {
                    return key;
                }
                return acc;
            }, "");

            acc.push({ key, label: header });

            return acc;
        },
        [] as { key: string; label: string }[],
    );

    return {
        accountSelectedColumns: columns.companyAndWebsite?.map(({ name }) => name) ?? [],
        selectedColumns: columns.contacts?.map(({ name }) => name) ?? [],
        rows,
        headers,
        fileName,
    };
};
